import * as React from "react";
import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cn } from "@/shadcn/lib/utils";
import { ChevronRightIcon, MoreHorizontalIcon } from "lucide-react";

// playerdex · registry v1 · shadcn@4.21.0

const Breadcrumb = React.forwardRef(function Breadcrumb(
  { className, ...props },
  ref
) {
  return (
    <nav
      aria-label="breadcrumb"
      data-slot="breadcrumb"
      className={cn(className)}
      ref={ref}
      {...props}
    />
  );
});

const BreadcrumbList = React.forwardRef(function BreadcrumbList(
  { className, ...props },
  ref
) {
  return (
    <ol
      data-slot="breadcrumb-list"
      className={cn(
        "text-muted-foreground gap-1.5 text-sm sm:gap-2.5 flex flex-wrap items-center wrap-break-word",
        className
      )}
      ref={ref}
      {...props}
    />
  );
});

const BreadcrumbItem = React.forwardRef(function BreadcrumbItem(
  { className, ...props },
  ref
) {
  return (
    <li
      data-slot="breadcrumb-item"
      className={cn("gap-1.5 inline-flex items-center", className)}
      ref={ref}
      {...props}
    />
  );
});

const BreadcrumbLink = React.forwardRef(function BreadcrumbLink(
  { className, render, ...props },
  ref
) {
  return useRender({
    ref,
    defaultTagName: "a",
    props: mergeProps(
      {
        className: cn("hover:text-foreground transition-colors", className),
      },
      props
    ),
    render,
    state: {
      slot: "breadcrumb-link",
    },
  });
});

const BreadcrumbPage = React.forwardRef(function BreadcrumbPage(
  { className, ...props },
  ref
) {
  return (
    <span
      data-slot="breadcrumb-page"
      role="link"
      aria-disabled="true"
      aria-current="page"
      className={cn("text-foreground font-normal", className)}
      ref={ref}
      {...props}
    />
  );
});

const BreadcrumbSeparator = React.forwardRef(function BreadcrumbSeparator(
  { children, className, ...props },
  ref
) {
  return (
    <li
      data-slot="breadcrumb-separator"
      role="presentation"
      aria-hidden="true"
      className={cn("[&>svg]:size-3.5", className)}
      ref={ref}
      {...props}
    >
      {children ?? <ChevronRightIcon />}
    </li>
  );
});

const BreadcrumbEllipsis = React.forwardRef(function BreadcrumbEllipsis(
  { className, ...props },
  ref
) {
  return (
    <span
      data-slot="breadcrumb-ellipsis"
      role="presentation"
      aria-hidden="true"
      className={cn(
        "size-5 [&>svg]:size-4 flex items-center justify-center",
        className
      )}
      ref={ref}
      {...props}
    >
      <MoreHorizontalIcon />
      <span className="sr-only">More</span>
    </span>
  );
});

export {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
};
